import React, { useState } from "react";
import SendTxTemplate from "./SendTxTemplate";
import { walletRepository } from "../../../../repositories/WalletRepository";
import { apiResponseToTransaction, Transaction } from "../../../../models/Transactions";
import { useWallet } from "../../../../hooks/useWallet";
import { InputSearch } from "../../../../components/InputSearch";
import { formatAmountWithSATS, localeFormatNumber } from "../../../../utils/Utils";
import { ButtonVariant } from "../../../../config/colors";
import { SendTransactionState } from "../SendTransaction";
import RadioButton from "../../../../components/RadioButton";
import Clickable from "../../../../components/Clickable";

interface SendTxStep3Props {
  transactionState: SendTransactionState;
  onChangeComment: (comment: string) => void;
  onValidateTransaction: (transaction: Transaction) => void;
  onChangeAnonymously: (anonymously: boolean) => void;
}

export const payFromTransactionState = async (transactionState: SendTransactionState): Promise<Transaction | null> => {     
    try {
        if (transactionState.recipient?.bulkPayment) {
            // Handle bulk payment
            const data = await walletRepository.payBulkPayment(
                transactionState.recipient.bulkPayment.type,
                transactionState.recipient.bulkPayment.id,
                transactionState.amount ?? 0,
                transactionState.comment ?? '',
                transactionState.anonymous ?? false
            );
            
            return apiResponseToTransaction(data);
        } else {
            // Handle LNURL payment
            const data = await walletRepository.payLnurl(
                transactionState.scanData?.lnurl || transactionState.scanData?.invoice || '', 
                transactionState.amount ?? 0, 
                transactionState.comment ?? '',
                transactionState.recipient?.pleb?.id ?? undefined,
                transactionState.anonymous ?? false
            );

            return apiResponseToTransaction(data);
        }
    } catch (err: any) {
        // Handle specific error cases
        if (err?.response?.data?.lockedFunds) {
            const untilDate = new Date(err.response.data.lockedFunds.until);
            const formattedDate = untilDate.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
            });
            
            throw new Error(`Your ${formatAmountWithSATS(err.response.data.lockedFunds.amountMsat / 1000)} will be available on ${formattedDate}`);
        } else if (err?.response?.data?.error) {
            throw new Error(err.response.data.error);
        } else {
            throw new Error('Unknown error');
        }
    }
};

const SendTxStep3: React.FC<SendTxStep3Props> = ({ transactionState, onChangeComment, onValidateTransaction, onChangeAnonymously }) => {
    const [loading, setLoading] = useState(false);
    const { refresh } = useWallet();
    const [error, setError] = useState('');

    const handleButtonClick = async () => {
        setLoading(true);
        setError('');

        try {
            const transaction = await payFromTransactionState(transactionState);
            if(transaction) {
                onValidateTransaction(transaction);
                refresh();
            }
        } catch (err: any) {
            setError(err.message);
        }

        setLoading(false);
    }

    const handleChangeComment = (comment: string) => {
        const maxLength = transactionState.scanData?.commentAllowed ?? 250;
        const croppedComment = comment.slice(0, maxLength);
        onChangeComment(croppedComment);
    }

    const getValueFieldTo = () => {
        if(transactionState.recipient?.bulkPayment)
            return transactionState.recipient.bulkPayment.getDisplayName();
        else if(transactionState.recipient?.lnurl)
            return transactionState.scanData?.lnurl;
        else if(transactionState.recipient?.invoice)
            return transactionState.scanData?.invoice;
        else
            return transactionState.recipient?.pleb?.getLabelToShowSendToWallet();
    }

    const getAmout = () : number => {
        if(transactionState.recipient?.bulkPayment)
            return transactionState.recipient.bulkPayment.recipientsCount * (transactionState.amount ?? 0);
        else
            return transactionState.amount ?? 0;
    }

    const blocks = [
        <div>
            <InputSearch
                placeholder="To"
                value={getValueFieldTo()}
                readOnly
            />
        </div>,
    ];

    if(
        (transactionState.scanData?.commentAllowed && transactionState.scanData?.commentAllowed > 0) ||
        transactionState.recipient?.bulkPayment
    )
        blocks.push(<div>
            <div className="mb-1 flex flex-row justify-between items-center">
                <div className="text-lg font-semibold">Message</div>
                <div className="text-sm text-brand-color-2">
                    {transactionState?.scanData?.commentAllowed && `Max ${transactionState.scanData.commentAllowed} characters`}
                </div>
            </div>
            <InputSearch
                placeholder="Message"
                value={transactionState.comment ?? ''}
                onClear={() => handleChangeComment('')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeComment(e.target.value)}
            />
        </div>)

    const opaFees = transactionState.scanData?.opaFeesInPercent ? (getAmout()) * (transactionState.scanData?.opaFeesInPercent / 100) : 0;

    blocks.push(
        <div className="flex flex-col gap-3">
            <div className="flex flex-row justify-between items-center">
                <div className="text-lg font-semibold">Amount</div>
                <div className="text-lg font-semibold">{formatAmountWithSATS(getAmout())}</div>
            </div>
            {
                opaFees > 0 && (
                    <>
                        <div className="flex flex-row justify-between items-center text-brand-color-2">
                            <div className="">Orange Pill App Fees ({(transactionState.scanData?.opaFeesInPercent ?? 0).toLocaleString(localeFormatNumber)}%)</div>
                            <div className="">{formatAmountWithSATS(opaFees)}</div>
                        </div>
                        <div className="flex flex-row justify-between items-center bg-brand-color-2 h-[1px] my-2"></div>
                        <div className="flex flex-row justify-between items-center">
                            <div className="text-lg font-semibold">Total Amount</div>
                            <div className="text-lg font-semibold">{formatAmountWithSATS((getAmout()) + opaFees)}</div>
                        </div>
                    </>
                )
            }
        </div>
        
    )

    return (
        <SendTxTemplate
            onButtonClick={handleButtonClick}
            buttonDisabled={false}
            buttonLoading={loading}
            blocks={blocks}
            error={error}
            buttonVariant={ButtonVariant.primary}
            buttonText="SEND IT"
            afterButton={
                (transactionState.scanData?.walletInternal !== undefined || transactionState.recipient?.bulkPayment) && (
                    <div className="flex flex-row justify-between items-center cursor-pointer gap-2 py-2" onClick={() => onChangeAnonymously(!transactionState.anonymous)}>
                        <div className="uppercase font-medium opacity-80">
                            Send anonymously
                        </div>
                        <RadioButton
                            checked={transactionState.anonymous ?? false}
                        />
                    </div>
                )
            }
        >
        </SendTxTemplate>
    );
}

export default SendTxStep3;